import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PATHS, USER_ROLES } from 'src/common/constants';
import { isRole } from 'src/helpers/permissions';
import { convertTime } from 'src/helpers/time';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { IServiceRequestDetail } from 'src/services/api/ServiceRequest/apiContracts';

interface ServiceRequestLinkProps {
  serviceRequest: IServiceRequestDetail;
  user?: any;
}

interface ServiceRequestLocationBreadcrumbProps extends ServiceRequestLinkProps {
  loading?: boolean;
}

const PropertyLink = ({ serviceRequest, user }: ServiceRequestLinkProps) => {
  const canLink = user && isRole(
    [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.PROPERTY_DEVELOPER],
    user
  );

  const linkTo = useMemo(() => ({
    pathname: PATHS.PROPERTIES_VIEW.replace(':id', serviceRequest.suite.property.id),
    state: {
      data: {
        id: serviceRequest.suite.property.id,
        serviceRequestId: serviceRequest.id,
      },
    },
  }), [serviceRequest.suite.property.id, serviceRequest.id]);

  if (!canLink) {
    return (
      <span className="serviceRequestDetails__links">
        {serviceRequest.suite.property.name}
      </span>
    );
  }

  return (
    <Link
      to={linkTo}
      className="serviceRequestDetails__links"
      style={{ textDecoration: 'underline', cursor: 'pointer' }}
    >
      {serviceRequest.suite.property.name}
    </Link>
  );
};

const SuiteLink = ({ serviceRequest, user }: ServiceRequestLinkProps) => {
  const canLink = user && isRole(
    [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.PROPERTY_DEVELOPER],
    user
  );

  const linkTo = useMemo(() => ({
    pathname: PATHS.SUITES_VIEW.replace(
      ':id',
      `${serviceRequest.id}&${serviceRequest.suite.id}&${serviceRequest.suite.property.id}`
    ),
    state: {
      data: { id: serviceRequest.suite.id, serviceRequestId: serviceRequest.id },
      suite: { id: serviceRequest.suite.id },
      property: { id: serviceRequest.suite.property.id },
      from: user && isRole(USER_ROLES.HOME_OWNER, user) ? 'Dashboard' : null,
    },
  }), [serviceRequest.id, serviceRequest.suite.id, serviceRequest.suite.property.id, user]);

  if (!canLink) {
    return (
      <span className="serviceRequestDetails__links">
        {serviceRequest.suite.number}
      </span>
    );
  }

  return (
    <Link
      to={linkTo}
      className="serviceRequestDetails__links"
      style={{ textDecoration: 'underline', cursor: 'pointer' }}
    >
      {serviceRequest.suite.number}
    </Link>
  );
};

const ServiceRequestLocationBreadcrumb = ({
  serviceRequest,
  user,
  loading,
}: ServiceRequestLocationBreadcrumbProps) => {
  if (loading) {
    return (
      <>
        <div className="serviceRequestDetails__breadcrumb">
          <Skeleton variant="text" width="8rem" />
          <span> / </span>
          <Skeleton variant="text" width="4rem" />
          <span> / </span>
          <Skeleton variant="text" width="6rem" />
        </div>
        <div style={{ padding: '10px 10px 10px 0' }}>
          Warranty start: <Skeleton variant="text" width="6rem" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="serviceRequestDetails__breadcrumb">
        <PropertyLink serviceRequest={serviceRequest} user={user} />
        <span> / </span>
        <SuiteLink serviceRequest={serviceRequest} user={user} />
        <span> / </span>
        <span
          className="serviceRequestDetails__links"
          style={{ textDecoration: 'none' }}
        >
          {serviceRequest.asset?.name}
        </span>
      </div>

      <div style={{ padding: '10px 10px 10px 0' }}>
        Warranty start:{' '}
        <span style={{ color: 'black' }}>
          {convertTime(serviceRequest.asset?.warrantyStartDate, 'date')}
        </span>
      </div>

      {serviceRequest.suite?.warrantyAddress && (
        <div style={{ paddingLeft: 0 }}>
          Warranty/Civic Address:
          <span style={{ color: 'black' }}>
            {' '}
            {serviceRequest.suite.warrantyAddress}
          </span>
        </div>
      )}
    </>
  );
};

export default ServiceRequestLocationBreadcrumb;
